import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MethodContent, MethodContentSection, SurveyQuestion } from '@/types/methodologies';

interface SurveyViewProps {
  methodId: string;
  content: MethodContent;
}

// Meta sections describe the survey as a whole (who/where/how it'll be
// analyzed); the questions themselves come from `content.questions`, not
// `content.sections` — a plain array of strings can't carry type/options/showIf.
const META_SECTION_KEYS = ['goal', 'sample', 'channels', 'analysis_plan'] as const;

const QUESTION_TYPE_LABEL_KEY: Record<SurveyQuestion['type'], string> = {
  single: 'survey.type.single',
  multiple: 'survey.type.multiple',
  scale: 'survey.type.scale',
  open: 'survey.type.open',
};

function questionLabel(questions: SurveyQuestion[], questionId: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const index = questions.findIndex((q) => q.id === questionId);
  if (index === -1) return questionId;
  return t('survey.questionNumber', { number: index + 1 });
}

/**
 * Renders the plain-text version used by "Скопировать анкету" — kept close
 * to how a respondent reads a form (Yandex Forms / Google Forms style):
 * numbered questions, options as a dash list, branching noted inline.
 */
function buildCopyText(
  content: MethodContent,
  questions: SurveyQuestion[],
  metaSections: MethodContentSection[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const lines: string[] = [];

  if (content.title) lines.push(content.title, '');

  for (const section of metaSections) {
    lines.push(`${t(`sections.${'survey'}.${section.key}`, { defaultValue: section.title })}:`);
    for (const item of section.items) lines.push(`  ${item}`);
    lines.push('');
  }

  questions.forEach((question, index) => {
    lines.push(`${index + 1}. ${question.text}`);
    if (question.showIf) {
      lines.push(
        `   (${t('survey.showIf', {
          question: questionLabel(questions, question.showIf.questionId, t),
          answer: question.showIf.answer,
        })})`,
      );
    }
    if (question.type === 'open') {
      lines.push(`   ${t('survey.type.open')}`);
    } else {
      for (const option of question.options ?? []) {
        lines.push(`   - ${option}`);
      }
    }
    lines.push('');
  });

  return lines.join('\n').trim();
}

function OptionMarker({ type }: { type: SurveyQuestion['type'] }) {
  if (type === 'multiple') {
    return <span aria-hidden="true" className="w-[16px] h-[16px] rounded-[4px] border border-[#969696] shrink-0" />;
  }
  return <span aria-hidden="true" className="w-[16px] h-[16px] rounded-full border border-[#969696] shrink-0" />;
}

function QuestionCard({ question, index, questions, t }: {
  question: SurveyQuestion;
  index: number;
  questions: SurveyQuestion[];
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <li className="bg-white border border-[#eeeeee] rounded-[16px] p-[20px] flex flex-col gap-[12px]">
      <div className="flex items-start justify-between gap-[12px]">
        <p className="font-manrope font-semibold text-[16px] leading-[24px] text-[#0d0d0d]">
          {index + 1}. {question.text}
        </p>
        <span className="shrink-0 font-segoe text-[12px] leading-[16px] text-[#565858] bg-[#f4f4f3] rounded-[8px] px-[8px] py-[4px]">
          {t(QUESTION_TYPE_LABEL_KEY[question.type])}
        </span>
      </div>

      {question.showIf && (
        <p className="font-segoe text-[13px] leading-[18px] text-[#969696] italic">
          {t('survey.showIf', {
            question: questionLabel(questions, question.showIf.questionId, t),
            answer: question.showIf.answer,
          })}
        </p>
      )}

      {question.type === 'open' ? (
        <div className="h-[40px] rounded-[8px] border border-dashed border-[#eeeeee]" aria-hidden="true" />
      ) : question.type === 'scale' ? (
        <div className="flex flex-wrap gap-[8px]">
          {(question.options ?? []).map((option, optionIndex) => (
            <span
              key={optionIndex}
              className="min-w-[40px] h-[32px] px-[8px] rounded-[8px] border border-[#eeeeee] flex items-center justify-center font-segoe text-[13px] text-[#565858]"
            >
              {option}
            </span>
          ))}
        </div>
      ) : (
        <ul className="flex flex-col gap-[8px]">
          {(question.options ?? []).map((option, optionIndex) => (
            <li key={optionIndex} className="flex items-center gap-[8px] font-segoe text-[14px] leading-[20px] text-[#0d0d0d]">
              <OptionMarker type={question.type} />
              {option}
            </li>
          ))}
        </ul>
      )}

      {question.purpose && (
        <p className="font-segoe text-[12px] leading-[16px] text-[#969696]">
          {t('survey.purposeLabel')}: {question.purpose}
        </p>
      )}
    </li>
  );
}

/**
 * The only new layout F-03 registers: draws the questionnaire the way a
 * respondent would see it (numbering, question type, options, "show if"
 * branching), plus a plain-text export sized for pasting into Yandex Forms
 * or Google Forms — collecting responses ourselves is out of scope for v1.
 */
export function SurveyView({ methodId, content }: SurveyViewProps) {
  const { t } = useTranslation('methodologies');
  const [copied, setCopied] = useState(false);
  const questions = content.questions ?? [];
  const sectionByKey = new Map<string, MethodContentSection>(content.sections.map((s) => [s.key, s]));
  const metaSections = META_SECTION_KEYS.map((key) => sectionByKey.get(key)).filter(
    (section): section is MethodContentSection => Boolean(section),
  );

  const handleCopy = async () => {
    const text = buildCopyText(content, questions, metaSections, t);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard permission denied or unavailable — the button simply won't
      // confirm; there's no fallback UI worth adding for this edge case.
    }
  };

  return (
    <div className="flex flex-col gap-[24px]">
      {metaSections.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px]">
          {metaSections.map((section) => (
            <div key={section.key} className="bg-[#f4f4f3] rounded-[16px] p-[20px] flex flex-col gap-[8px]">
              <h3 className="font-manrope font-semibold text-[13px] leading-[18px] text-[#565858] uppercase">
                {t(`sections.${methodId}.${section.key}`, { defaultValue: section.title })}
              </h3>
              <ul className="flex flex-col gap-[6px]">
                {section.items.map((item, index) => (
                  <li key={index} className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d]">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {questions.length > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-[8px] rounded-[16px] px-[16px] py-[10px] bg-[#f4f4f3] hover:bg-[#eeeeee] transition-colors font-segoe font-semibold text-[14px] leading-[20px] text-[#0d0d0d]"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M15 5H9a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-1" stroke="#0d0d0d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 2H8a2 2 0 0 0-2 2v3" stroke="#0d0d0d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {copied ? t('survey.copied') : t('survey.copyButton')}
          </button>
        </div>
      )}

      <ol className="flex flex-col gap-[16px]">
        {questions.map((question, index) => (
          <QuestionCard key={question.id} question={question} index={index} questions={questions} t={t} />
        ))}
      </ol>
    </div>
  );
}
